import type { SentArgumentsDto } from '@dbrain/shared';

/**
 * D-178 (OnayKartiAyrinti.dc.html): what a connection's tool call sends,
 * as rows a person can read. A field's name is the input schema's title,
 * else its key made readable ("assignee_id" → "Assignee id"). Texts keep
 * every character; long ones are folded behind "Tamamını göster". Yes/no,
 * lists and small nested blocks are shown as such. The raw JSON is there
 * too. When the server had to shorten the input, each shortened text says
 * how much is missing and the card says so; nothing is cut silently.
 */
export type SentValue =
  | { kind: 'text'; text: string; long: boolean; cut: number | null }
  | { kind: 'number'; text: string }
  | { kind: 'bool'; value: boolean }
  | { kind: 'empty' }
  | { kind: 'list'; items: SentValue[] }
  | { kind: 'object'; rows: SentRow[] };

export interface SentRow {
  key: string;
  label: string;
  value: SentValue;
}

export interface SentView {
  connection: { name: string; tool: string };
  rows: SentRow[];
  /** the input as sent, indented */
  raw: string;
  truncated: { shown: number; total: number } | null;
}

/** a text folds behind "Tamamını göster" from this many characters or lines on */
export const LONG_TEXT_CHARS = 280;
export const LONG_TEXT_LINES = 4;

const CUT = /… \[(\d+)\]$/;

/** "assignee_id", "assigneeId", "assignee-id" → "Assignee id" */
export function humanizeKey(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_\-.]+/g, ' ')
    .trim()
    .toLowerCase();
  if (!words) return key;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

interface SchemaNode {
  title?: string;
  properties?: Record<string, SchemaNode>;
  items?: SchemaNode;
}

function valueOf(value: unknown, schema: SchemaNode | undefined, truncated: boolean): SentValue {
  if (value === null || value === undefined || value === '') return { kind: 'empty' };
  if (typeof value === 'boolean') return { kind: 'bool', value };
  if (typeof value === 'number') return { kind: 'number', text: String(value) };
  if (typeof value === 'string') {
    const match = truncated ? CUT.exec(value) : null;
    const text = match ? value.slice(0, match.index) : value;
    return { kind: 'text', text, long: text.length >= LONG_TEXT_CHARS || text.split('\n').length > LONG_TEXT_LINES, cut: match ? Number(match[1]) : null };
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return { kind: 'empty' };
    return { kind: 'list', items: value.map((item) => valueOf(item, schema?.items, truncated)) };
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return { kind: 'empty' };
    return { kind: 'object', rows: rowsOf(value as Record<string, unknown>, schema, truncated) };
  }
  return { kind: 'text', text: String(value), long: false, cut: null };
}

function rowsOf(input: Record<string, unknown>, schema: SchemaNode | undefined, truncated: boolean): SentRow[] {
  return Object.entries(input).map(([key, value]) => {
    const field = schema?.properties?.[key];
    return { key, label: field?.title?.trim() || humanizeKey(key), value: valueOf(value, field, truncated) };
  });
}

function parse<T>(text: string | undefined): T | undefined {
  if (!text) return undefined;
  try {
    return JSON.parse(text) as T;
  } catch {
    return undefined;
  }
}

export function sentView(sent: SentArgumentsDto): SentView {
  const truncated = sent.truncated ?? null;
  const input = parse<unknown>(sent.arguments);
  const schema = parse<SchemaNode>(sent.schema);
  // a structure cut as text cannot be read as rows: the raw text alone
  if (input === undefined) return { connection: sent.connection, rows: [], raw: sent.arguments, truncated };
  const raw = JSON.stringify(input, null, 2);
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    return { connection: sent.connection, rows: [{ key: '', label: '', value: valueOf(input, schema, truncated !== null) }], raw, truncated };
  }
  return { connection: sent.connection, rows: rowsOf(input as Record<string, unknown>, schema, truncated !== null), raw, truncated };
}

/** How many fields a nested block holds, for its folded line ("3 alan"). */
export function fieldCount(value: SentValue): number {
  return value.kind === 'object' ? value.rows.length : value.kind === 'list' ? value.items.length : 0;
}

interface SentCard {
  /** the tool the card asks about, as the model called it (`mcp__<server>__<tool>`, `remember`…) */
  tool: string;
  sent: SentArgumentsDto | null | undefined;
}

/**
 * The connection a card's input goes to, for its "Bağlantı: … · araç: …" line and the phone's connection card: only
 * a connection's tool (`mcp__…`) has one (named as the server named it, else by the tool). One of Oppy's own tools
 * may show its input on its card too (D-199: `remember` shows the text it would keep once a conversation read
 * Microsoft 365 content); it names no connection, whatever the card carries.
 */
export function sentConnection(card: SentCard): { name: string; tool: string } | null {
  if (!card.sent || !card.tool.startsWith('mcp__')) return null;
  const given = card.sent.connection as Partial<SentArgumentsDto['connection']> | undefined;
  const name = typeof given?.name === 'string' && given.name.trim() ? given.name : card.tool;
  return { name, tool: typeof given?.tool === 'string' && given.tool.trim() ? given.tool : card.tool };
}

/** The words over a card's input: what leaves Oppy (a connection's tool, the web), what `remember` keeps, what another of Oppy's tools uses. */
export type SentIntro = 'send' | 'remember' | 'use';

const SENDS_OUT = new Set(['web_search', 'web_fetch']);

export function sentIntro(card: SentCard): SentIntro | null {
  if (!card.sent) return null;
  if (sentConnection(card) || SENDS_OUT.has(card.tool)) return 'send';
  return card.tool === 'remember' ? 'remember' : 'use';
}
